/**
 * Playoff clinch and elimination detection.
 *
 * For each team, we report at most one mathematical "status":
 *
 *  - `division`   — guaranteed to finish in the top-K of their own division
 *                   (where K = `playoffStructure.divisionWinnersPerDivision`).
 *  - `playoff`    — guaranteed a playoff seat (division winner OR wildcard)
 *                   but not necessarily a division-winner seat.
 *  - `eliminated` — mathematically cannot reach any playoff seat.
 *
 * All checks are conservative: we under-declare clinch and under-declare elimination.
 * Sources of conservatism:
 *
 *   1. Tiebreakers beyond `wins` (diff / touchdowns for / casualties for) are not
 *      bounded — score margins can be arbitrarily large. When two teams tie on all
 *      bounded tiebreakers, we pessimistically assume the unbounded tail is
 *      unresolved (treated as "could pass us" for clinch, "may not pass us" for
 *      elimination).
 *   2. Rivals at their ceilings/floors are evaluated largely independently. We
 *      have joint-constraint tightenings where they're safe (see
 *      `countNonDivisionWinnersAboveWorstCase` for wildcard analysis), but
 *      rival-vs-rival head-to-head schedule constraints (e.g., two rivals play
 *      each other so they can't both win out) are not modelled. A full
 *      LP / constraint-based analysis could recover more clinches and eliminations.
 *
 * Organization:
 *   - `getRemainingGamesByTeam`   — schedule scan (total + within-division split)
 *   - `couldRivalOutrank`         — "can rival end above this team?"      (ceiling vs floor)
 *   - `mustRivalOutrank`          — "must rival end above this team?"     (ceiling vs floor, flipped)
 *   - `getDivisionClinch`         — is this team locked into top-K of their division?
 *   - `getWildcardClinch`         — is this team locked into a playoff seat overall?
 *   - `getEliminationStatus`      — is this team mathematically out of contention?
 *   - `getClinchReason` / `getClinchStatuses` — public entry points.
 */

import { Division as DivisionModel, Week as WeekModel, PlayoffStructure } from '../model/Season';
import { TeamStats, isRegularSeasonWeekName } from './seasonStandingsCore';

const POINTS_PER_WIN = 3;

export type ClinchStatus =
    /** Locked into a top-K division-winner seat. */
    | 'division'
    /** Locked into some playoff seat (may or may not be division winner). */
    | 'playoff'
    /** Mathematically unable to reach any playoff seat. */
    | 'eliminated';

export interface ClinchReason {
    status: ClinchStatus;
    /** Short, passive-voice explanation suitable for tooltips. */
    message: string;
}

/** Per-team remaining-game counts, broken out so we can bound divisionPoints tightly. */
export interface TeamRemaining {
    /** All unplayed regular-season matches for this team. */
    total: number;
    /** Subset of {@link total} whose opponent is in the same division (used to bound divisionPoints). */
    sameDivision: number;
}

export interface ClinchInput {
    divisions: DivisionModel[];
    weeks: WeekModel[];
}

// ─── Schedule accounting ──────────────────────────────────────────────────────

function buildTeamDivisionMap(divisions: DivisionModel[] | undefined): Record<string, string> {
    const map: Record<string, string> = {};
    if (!Array.isArray(divisions)) return map;
    for (const div of divisions) {
        if (!div?.divisionName || !Array.isArray(div.teams)) continue;
        for (const team of div.teams) {
            if (team?.teamId) map[team.teamId] = div.divisionName;
        }
    }
    return map;
}

/**
 * Build per-team remaining game counts from the raw schedule.
 * A match counts as "remaining" when it's on a regular-season week and has no results.
 */
export function getRemainingGamesByTeam(input: ClinchInput | null | undefined): Record<string, TeamRemaining> {
    const out: Record<string, TeamRemaining> = {};
    if (!input) return out;
    const divByTeam = buildTeamDivisionMap(input.divisions);
    const bump = (teamId: string, sameDiv: boolean) => {
        if (!out[teamId]) out[teamId] = { total: 0, sameDivision: 0 };
        out[teamId].total += 1;
        if (sameDiv) out[teamId].sameDivision += 1;
    };
    const weeks = Array.isArray(input.weeks) ? input.weeks : [];
    for (const week of weeks) {
        if (!week?.weekName || !isRegularSeasonWeekName(week.weekName)) continue;
        if (!Array.isArray(week.matches)) continue;
        for (const match of week.matches) {
            if (!match || match.results) continue;
            const { homeTeamId, awayTeamId } = match;
            const homeDiv = homeTeamId ? divByTeam[homeTeamId] : undefined;
            const awayDiv = awayTeamId ? divByTeam[awayTeamId] : undefined;
            const sameDiv = !!homeDiv && homeDiv === awayDiv;
            if (homeTeamId) bump(homeTeamId, sameDiv);
            if (awayTeamId) bump(awayTeamId, sameDiv);
        }
    }
    return out;
}

function remainingFor(map: Record<string, TeamRemaining>, teamId: string): TeamRemaining {
    return map[teamId] ?? { total: 0, sameDivision: 0 };
}

/** Max points a team could still finish with (assume they win every remaining game). */
export function maxPossiblePoints(team: TeamStats, remaining: TeamRemaining): number {
    return team.points + POINTS_PER_WIN * Math.max(0, remaining.total);
}

// ─── Tiebreaker-aware outranking checks ───────────────────────────────────────

/**
 * Returns `true` when there exists a remaining-games scenario in which `rival`
 * finishes ranked above `team`. Compares `rival`'s ceiling against `team`'s floor
 * using bounded tiebreakers (points → divPoints (same-div only) → wins); if all
 * bounded tiebreakers are tied, falls through pessimistically (unbounded
 * diff/TF/cas tiebreakers might go either way — caller must assume the worst).
 *
 * Why the comparison works: if `rival`'s max points exactly equal `team`'s current
 * points, that pins the scenario — `team` loses every remaining game and `rival`
 * wins every remaining game — which also pins `rival`'s divisionPoints/wins to
 * their max and `team`'s to their current values. Comparing max-to-current is
 * therefore the correct tiebreaker comparison in the pinned scenario.
 */
export function couldRivalOutrank(
    team: TeamStats,
    rival: TeamStats,
    rivalRemaining: TeamRemaining,
    sameDivision: boolean
): boolean {
    const rivalMaxPoints = rival.points + POINTS_PER_WIN * rivalRemaining.total;
    if (rivalMaxPoints > team.points) return true;
    if (rivalMaxPoints < team.points) return false;

    if (sameDivision) {
        const rivalMaxDivPoints = rival.divisionPoints + POINTS_PER_WIN * rivalRemaining.sameDivision;
        if (rivalMaxDivPoints > team.divisionPoints) return true;
        if (rivalMaxDivPoints < team.divisionPoints) return false;
    }

    const rivalMaxWins = rival.wins + rivalRemaining.total;
    if (rivalMaxWins > team.wins) return true;
    if (rivalMaxWins < team.wins) return false;

    // Beyond wins are diff / TF / Cas, which have no hard upper bound; be pessimistic toward clinch.
    return true;
}

/**
 * Dual of {@link couldRivalOutrank}: returns `true` when `rival` is guaranteed to
 * finish above `team` in every remaining-games scenario. Compares `team`'s
 * ceiling against `rival`'s floor; ties on all bounded tiebreakers fall through
 * pessimistically toward "not guaranteed" (unbounded diff/TF/cas could still
 * swing the other way).
 *
 * Symmetry note: `couldRivalOutrank` rejects only when it can *prove* the team
 * still beats the rival; `mustRivalOutrank` accepts only when it can *prove* the
 * rival still beats the team. The two together cover clinch and elimination.
 */
export function mustRivalOutrank(
    team: TeamStats,
    rival: TeamStats,
    teamRemaining: TeamRemaining,
    sameDivision: boolean
): boolean {
    const teamMaxPoints = team.points + POINTS_PER_WIN * teamRemaining.total;
    if (rival.points > teamMaxPoints) return true;
    if (rival.points < teamMaxPoints) return false;

    if (sameDivision) {
        const teamMaxDivPoints = team.divisionPoints + POINTS_PER_WIN * teamRemaining.sameDivision;
        if (rival.divisionPoints > teamMaxDivPoints) return true;
        if (rival.divisionPoints < teamMaxDivPoints) return false;
    }

    const teamMaxWins = team.wins + teamRemaining.total;
    if (rival.wins > teamMaxWins) return true;
    if (rival.wins < teamMaxWins) return false;

    // Beyond wins are unbounded; cannot guarantee rival still outranks team.
    return false;
}

// ─── Internal: collect division context ───────────────────────────────────────

interface DivisionThreats {
    /** Same-division teams (other than `team`) that could finish ranked above `team` within division. */
    rivals: TeamStats[];
    /** Teams grouped by *other* division, with only rivals that could finish above `team` in playoff-pool ordering. */
    otherDivRivalsByDivision: Record<string, TeamStats[]>;
    /** Count of teams in each division (including `team`'s division). */
    divisionSizes: Record<string, number>;
}

function collectThreats(
    team: TeamStats,
    standings: Record<string, TeamStats[]>,
    remaining: Record<string, TeamRemaining>
): DivisionThreats {
    const rivals: TeamStats[] = [];
    const otherDivRivalsByDivision: Record<string, TeamStats[]> = {};
    const divisionSizes: Record<string, number> = {};

    for (const [divName, teams] of Object.entries(standings)) {
        divisionSizes[divName] = teams.length;
        const sameDiv = divName === team.divisionName;
        for (const rival of teams) {
            if (rival.teamId === team.teamId) continue;
            const couldPass = couldRivalOutrank(team, rival, remainingFor(remaining, rival.teamId), sameDiv);
            if (!couldPass) continue;
            if (sameDiv) {
                rivals.push(rival);
            } else {
                (otherDivRivalsByDivision[divName] ??= []).push(rival);
            }
        }
    }
    return { rivals, otherDivRivalsByDivision, divisionSizes };
}

// ─── Division clinch ──────────────────────────────────────────────────────────

/**
 * Is `team` locked into a top-K spot in their own division?
 * Returns `null` if not clinched (or if K < 1).
 */
export function getDivisionClinch(
    team: TeamStats,
    divisionRivals: TeamStats[],
    K: number
): ClinchReason | null {
    if (K < 1) return null;
    if (divisionRivals.length >= K) return null;

    const seatLabel = K === 1 ? 'Division clinched' : `Top-${K} seed clinched`;
    const message = divisionRivals.length === 0
        ? `${seatLabel}: no ${team.divisionName} rival can reach ${team.points} pts.`
        : `${seatLabel}: only ${divisionRivals.length} ${team.divisionName} rival(s) still in range for ${K} seat(s).`;
    return { status: 'division', message };
}

// ─── Playoff (wildcard) clinch ────────────────────────────────────────────────

/**
 * Can `candidate` possibly finish OUTSIDE the top-K of their own division when
 * they are *also* above a reference `floorPoints` threshold? Requires at least K
 * other teams in the division that could (a) rank above `candidate` within-division
 * and (b) themselves be above the `floorPoints` threshold on points (otherwise they
 * could not simultaneously outrank `candidate` in a scenario that also has `candidate`
 * above the threshold).
 *
 * When `floorPoints` is omitted, behaves as the generic "could be non-division-winner"
 * check (without the simultaneity constraint against a candidate team's floor).
 */
function couldBeNonDivisionWinner(
    candidate: TeamStats,
    divisionTeams: TeamStats[],
    remaining: Record<string, TeamRemaining>,
    K: number,
    floorPoints?: number
): boolean {
    if (K <= 0) return true;
    let countAbove = 0;
    for (const other of divisionTeams) {
        if (other.teamId === candidate.teamId) continue;
        // A rival T can only rank above `candidate` in a scenario where `candidate` is above
        // `floorPoints` if T can *also* clear `floorPoints` on points. Skip otherwise.
        if (floorPoints !== undefined) {
            const otherMaxPoints = other.points + POINTS_PER_WIN * (remainingFor(remaining, other.teamId).total);
            if (otherMaxPoints < floorPoints) continue;
        }
        if (couldRivalOutrank(candidate, other, remainingFor(remaining, other.teamId), true)) {
            countAbove += 1;
            if (countAbove >= K) return true;
        }
    }
    return false;
}

/**
 * When every non-rival in `divisionTeams` has a maximum possible points total strictly
 * below `team.points`, the top-K of that division are forced to consist only of rivals
 * in any scenario where rivals have reached `team.points`. That lets us apply the tight
 * joint bound `max(0, |rivalsInDiv| − K)` (at most that many rivals can simultaneously
 * be non-division-winners above `team`).
 *
 * Strict inequality is required so the tighter bound stays safe around tiebreaker
 * edge cases where a non-rival with tied max points could sneak into top-K via the
 * within-division divPoints tiebreaker.
 */
function allNonRivalsStrictlyBelowOnPoints(
    team: TeamStats,
    divisionTeams: TeamStats[],
    rivalsInDiv: TeamStats[],
    remaining: Record<string, TeamRemaining>
): boolean {
    const rivalIds = new Set(rivalsInDiv.map(r => r.teamId));
    for (const other of divisionTeams) {
        if (other.teamId === team.teamId || rivalIds.has(other.teamId)) continue;
        const max = other.points + POINTS_PER_WIN * remainingFor(remaining, other.teamId).total;
        if (max >= team.points) return false;
    }
    return true;
}

/**
 * Worst-case count of non-division-winners that could end up ranked above `team`
 * in playoff-pool ordering.
 *
 * Two contributions:
 *   - **Same division**: if more than K same-division rivals could pass us, the excess are
 *     necessarily non-division-winners above us (top-K take the K division-winner seats).
 *   - **Other divisions**: two bounds, combined via `min` for the tightest valid estimate:
 *       * Per-rival bound — sum of rivals in that division that could individually be
 *         non-division-winners *while also* above `team.points`. This handles cases like
 *         "Vulcan at their ceiling is the only Shield Maiden team ≥ SoDo's floor, so Vulcan
 *         is locked into division-winner if they're above SoDo".
 *       * Joint bound — when every non-rival in that division is strictly below `team.points`
 *         on maximum possible points, the top-K of that division are forced to come from the
 *         rivals-above-team pool. In that case at most `|rivalsInDiv| − K` rivals can
 *         simultaneously be non-division-winners above `team`.
 *
 * A team that's locked into their division's top-K is a division winner — they take a
 * different seat, not a wildcard, and don't count toward non-division-winners above `team`.
 */
function countNonDivisionWinnersAboveWorstCase(
    team: TeamStats,
    threats: DivisionThreats,
    standings: Record<string, TeamStats[]>,
    remaining: Record<string, TeamRemaining>,
    K: number
): { total: number; byDivision: Record<string, number>; sameDivBumped: number } {
    const sameDivBumped = Math.max(0, threats.rivals.length - K);

    const byDivision: Record<string, number> = {};
    let otherDivBounded = 0;
    for (const [divName, rivalsInDiv] of Object.entries(threats.otherDivRivalsByDivision)) {
        const divTeams = standings[divName] ?? [];

        let perRival = 0;
        for (const rival of rivalsInDiv) {
            if (couldBeNonDivisionWinner(rival, divTeams, remaining, K, team.points)) perRival += 1;
        }

        let count = perRival;
        if (allNonRivalsStrictlyBelowOnPoints(team, divTeams, rivalsInDiv, remaining)) {
            count = Math.min(count, Math.max(0, rivalsInDiv.length - K));
        }

        byDivision[divName] = count;
        otherDivBounded += count;
    }
    return { total: sameDivBumped + otherDivBounded, byDivision, sameDivBumped };
}

/**
 * Is the team represented by `threats` guaranteed some playoff seat (division winner OR wildcard)?
 * Caller should use this only after `getDivisionClinch` returned `null` (the 'division' status is strictly stronger).
 */
export function getWildcardClinch(
    team: TeamStats,
    threats: DivisionThreats,
    standings: Record<string, TeamStats[]>,
    remaining: Record<string, TeamRemaining>,
    K: number,
    W: number
): ClinchReason | null {
    if (W < 1) return null;

    const { total: worstCaseNonWinnersAbove } = countNonDivisionWinnersAboveWorstCase(team, threats, standings, remaining, K);
    if (worstCaseNonWinnersAbove >= W) return null;

    const message = worstCaseNonWinnersAbove === 0
        ? `Playoff spot clinched: no non-division-winner can finish higher.`
        : `Playoff spot clinched: at most ${worstCaseNonWinnersAbove} non-division-winner(s) could finish higher — fewer than ${W} wildcard seat(s).`;
    return { status: 'playoff', message };
}

// ─── Elimination ──────────────────────────────────────────────────────────────

/**
 * Is `team` mathematically eliminated from playoff contention?
 *
 * Elimination requires both:
 *  1. At least K teams in the team's own division are guaranteed to finish above them
 *     (their division-winner seat is out of reach).
 *  2. At least W non-division-winners are guaranteed to finish above them league-wide
 *     (every wildcard seat is out of reach).
 *
 * Count (2) uses a per-division lower bound: in every scenario, at least
 * `max(0, |must-above in d| − K)` teams above the candidate in division d are
 * non-division-winners (top-K take the K division-winner seats; the remainder,
 * if any, are non-division-winners above the candidate). Summing across all
 * divisions gives a valid lower bound on total guaranteed non-division-winners
 * above the candidate.
 */
export function getEliminationStatus(
    team: TeamStats,
    standings: Record<string, TeamStats[]>,
    remaining: Record<string, TeamRemaining>,
    playoffStructure: PlayoffStructure | undefined
): ClinchReason | null {
    if (!playoffStructure) return null;
    const K = Math.max(0, Math.floor(playoffStructure.divisionWinnersPerDivision ?? 0));
    const W = Math.max(0, Math.floor(playoffStructure.wildcardCount ?? 0));
    if (K <= 0 && W <= 0) return null;

    const teamRemaining = remainingFor(remaining, team.teamId);

    // Count rivals per division that are guaranteed to finish above `team`.
    const mustAboveByDivision: Record<string, number> = {};
    for (const [divName, teams] of Object.entries(standings)) {
        const sameDiv = divName === team.divisionName;
        let count = 0;
        for (const rival of teams) {
            if (rival.teamId === team.teamId) continue;
            if (mustRivalOutrank(team, rival, teamRemaining, sameDiv)) count += 1;
        }
        mustAboveByDivision[divName] = count;
    }

    // Division-winner route: if fewer than K own-division rivals must finish above, that seat is still reachable.
    const mustAboveInOwn = mustAboveByDivision[team.divisionName] ?? 0;
    if (K > 0 && mustAboveInOwn < K) return null;

    // Wildcard route: per-division lower bound on guaranteed non-division-winners above.
    const guaranteedNonDivWinnersAbove = Object.values(mustAboveByDivision)
        .reduce((sum, count) => sum + Math.max(0, count - K), 0);
    if (guaranteedNonDivWinnersAbove < W) return null;

    const message = K > 0 && W > 0
        ? `Eliminated: at least ${mustAboveInOwn} ${team.divisionName} rival(s) and ${guaranteedNonDivWinnersAbove} non-division-winner(s) are guaranteed to finish higher.`
        : K > 0
            ? `Eliminated: at least ${mustAboveInOwn} ${team.divisionName} rival(s) are guaranteed to finish higher.`
            : `Eliminated: at least ${guaranteedNonDivWinnersAbove} team(s) are guaranteed to finish higher.`;
    return { status: 'eliminated', message };
}

// ─── Public entry points ──────────────────────────────────────────────────────

/**
 * Playoff status for a single team. Returns at most one status, preferring
 * `division` > `playoff` > `eliminated`. Returns `null` when the team's fate
 * is still undecided.
 */
export function getClinchReason(
    team: TeamStats,
    standings: Record<string, TeamStats[]>,
    remaining: Record<string, TeamRemaining>,
    playoffStructure: PlayoffStructure | undefined
): ClinchReason | null {
    if (!playoffStructure) return null;
    const K = Math.max(0, Math.floor(playoffStructure.divisionWinnersPerDivision ?? 0));
    const W = Math.max(0, Math.floor(playoffStructure.wildcardCount ?? 0));
    if (K <= 0 && W <= 0) return null;

    const threats = collectThreats(team, standings, remaining);

    const divisionClinch = getDivisionClinch(team, threats.rivals, K);
    if (divisionClinch) return divisionClinch;

    const wildcardClinch = getWildcardClinch(team, threats, standings, remaining, K, W);
    if (wildcardClinch) return wildcardClinch;

    return getEliminationStatus(team, standings, remaining, playoffStructure);
}

/**
 * Compute clinch reasons for every team in the standings.
 * @returns Map keyed by teamId. Teams that have not clinched are omitted.
 */
export function getClinchStatuses(
    standings: Record<string, TeamStats[]>,
    seasonData: ClinchInput,
    playoffStructure: PlayoffStructure | undefined
): Record<string, ClinchReason> {
    const result: Record<string, ClinchReason> = {};
    if (!playoffStructure) return result;

    const remaining = getRemainingGamesByTeam(seasonData);
    for (const teams of Object.values(standings)) {
        if (!Array.isArray(teams)) continue;
        for (const team of teams) {
            const reason = getClinchReason(team, standings, remaining, playoffStructure);
            if (reason) result[team.teamId] = reason;
        }
    }
    return result;
}
